import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FC,
  type KeyboardEvent,
} from "react";
import {
  HistoryOutlined,
  TeamOutlined,
  WarningFilled,
} from "@ant-design/icons";
import { formatDate } from "../../../../utils/dateService";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import type { EndSeasonAudit } from "../../../../types/gamePass";
import { Button, Input, Modal } from "../../../shared";

import styles from "./EndSeasonConfirmModal.module.css";

export type EndSeasonConfirmModalProps = {
  open: boolean;
  /** Fired when the admin dismisses via Cancel, the close button,
   *  backdrop, or escape. */
  onClose: () => void;
  /** Fired only when every field is filled and the confirmation keyword
   *  matches — the parent runs the end-season mutation with the captured
   *  audit fields. */
  onConfirm: (confirmation: EndSeasonAudit) => void;
  /** Shows the CTA loader / disables re-submission while the parent's
   *  end-season request is in flight. */
  isPending?: boolean;
};

const DATE_FORMAT = "YYYY-MM-DD";

// Native `<input type="date">` value format — not a display date, so
// timezone conversion doesn't apply here.
const todayDateValue = (): string => formatDate(new Date(), DATE_FORMAT);

/**
 * Production-grade destructive confirmation for ending the active Game
 * Pass season (FE-2247). The CTA stays disabled until the admin fills
 * their first / last name, a takedown date, and types the confirmation
 * keyword exactly — pressing Enter in the keyword field submits once
 * everything is valid.
 */
const EndSeasonConfirmModal: FC<EndSeasonConfirmModalProps> = ({
  open,
  onClose,
  onConfirm,
  isPending = false,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [takedownDate, setTakedownDate] = useState(todayDateValue);
  const [confirmText, setConfirmText] = useState("");

  // Fresh audit fields every time the modal opens — a previous aborted
  // attempt must not leave the CTA pre-armed.
  useEffect(() => {
    if (open) {
      setFirstName("");
      setLastName("");
      setTakedownDate(todayDateValue());
      setConfirmText("");
    }
  }, [open]);

  const keyword = AppStrings.END_SEASON_PROD_CONFIRM_KEYWORD;
  const isFirstNameMissing = firstName.trim().length === 0;
  const isLastNameMissing = lastName.trim().length === 0;
  const isConfirmed = confirmText === keyword;
  // `YYYY-MM-DD` strings compare correctly lexicographically, so this
  // rejects both empty and past dates (min attr alone doesn't stop a
  // manually typed past date from arming the CTA).
  const minTakedownDate = todayDateValue();
  const isDateValid = takedownDate >= minTakedownDate;
  const canSubmit = useMemo(
    () =>
      !isFirstNameMissing &&
      !isLastNameMissing &&
      isDateValid &&
      isConfirmed &&
      !isPending,
    [isFirstNameMissing, isLastNameMissing, isDateValid, isConfirmed, isPending],
  );

  const handleSubmit = useCallback(() => {
    if (canSubmit) {
      onConfirm({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        takedownDate,
      });
    }
  }, [canSubmit, onConfirm, firstName, lastName, takedownDate]);

  const handleConfirmKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") handleSubmit();
    },
    [handleSubmit],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      intent="destructive"
      size="md"
      // Tall form — antd's default 100px top offset pushes the footer
      // below the fold, so center it in the viewport instead.
      centered
      destroyOnHidden
      title={null}
      footer={
        <div className={styles.footer}>
          <Button
            intent="neutral"
            variant="outline"
            onClick={onClose}
            data-testid="season-details-end-season-go-back"
          >
            {AppStrings.CANCEL}
          </Button>
          <Button
            intent="danger"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={isPending}
            data-testid="season-details-end-season-confirm"
          >
            {AppStrings.SEASON_DETAILS_END_SEASON_CONFIRM_CTA}
          </Button>
        </div>
      }
    >
      <div data-testid="season-details-end-season-modal">
        <div className={styles.banner} data-testid="end-season-prod-banner">
          <WarningFilled className={styles.bannerIcon} aria-hidden="true" />
          <span>{AppStrings.END_SEASON_PROD_BANNER}</span>
        </div>

        <span className={styles.badge}>{AppStrings.END_SEASON_PROD_BADGE}</span>

        <h2 className={styles.title}>{AppStrings.END_SEASON_PROD_TITLE}</h2>
        <p className={styles.description}>{AppStrings.END_SEASON_PROD_BODY}</p>

        <div className={styles.impact} role="alert">
          <TeamOutlined className={styles.impactIcon} aria-hidden="true" />
          <span>{AppStrings.END_SEASON_PROD_IMPACT}</span>
        </div>

        <div className={styles.nameRow}>
          <div className={styles.field}>
            <label
              className={styles.fieldLabel}
              htmlFor="end-season-first-name"
            >
              {AppStrings.END_SEASON_PROD_FIRST_NAME}
            </label>
            <Input
              id="end-season-first-name"
              data-testid="end-season-first-name"
              className={styles.input}
              placeholder={AppStrings.END_SEASON_PROD_FIRST_NAME_PLACEHOLDER}
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
            />
            {isFirstNameMissing && (
              <span className={styles.requiredHint}>
                {AppStrings.END_SEASON_PROD_REQUIRED}
              </span>
            )}
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="end-season-last-name">
              {AppStrings.END_SEASON_PROD_LAST_NAME}
            </label>
            <Input
              id="end-season-last-name"
              data-testid="end-season-last-name"
              className={styles.input}
              placeholder={AppStrings.END_SEASON_PROD_LAST_NAME_PLACEHOLDER}
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
            />
            {isLastNameMissing && (
              <span className={styles.requiredHint}>
                {AppStrings.END_SEASON_PROD_REQUIRED}
              </span>
            )}
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="end-season-date">
            {AppStrings.END_SEASON_PROD_DATE}
          </label>
          <input
            id="end-season-date"
            data-testid="end-season-date"
            className={styles.dateInput}
            type="date"
            min={minTakedownDate}
            value={takedownDate}
            onChange={(event) => setTakedownDate(event.target.value)}
          />
        </div>

        <div className={styles.divider} />

        <p className={styles.confirmHint}>
          {AppStrings.END_SEASON_PROD_CONFIRM_HINT_PREFIX}{" "}
          <span className={styles.confirmKeyword}>{keyword}</span>{" "}
          {AppStrings.END_SEASON_PROD_CONFIRM_HINT_SUFFIX}
        </p>
        <Input
          data-testid="end-season-confirm-input"
          className={styles.input}
          placeholder={AppStrings.END_SEASON_PROD_CONFIRM_PLACEHOLDER}
          aria-label={AppStrings.END_SEASON_PROD_CONFIRM_PLACEHOLDER}
          value={confirmText}
          onChange={(event) => setConfirmText(event.target.value)}
          onKeyDown={handleConfirmKeyDown}
        />

        <div className={styles.auditNote}>
          <HistoryOutlined className={styles.auditIcon} aria-hidden="true" />
          <span>{AppStrings.END_SEASON_PROD_AUDIT_NOTE}</span>
        </div>
      </div>
    </Modal>
  );
};

export default EndSeasonConfirmModal;
